// Sales CRM API client — contract: docs/api/sales-crm.md
// Base: /api/v1/sales-crm, every request carries Accept: application/json
// and X-Workspace-Id (except the workspace/invite bootstrap routes).
import Vue from 'vue'
import axios from 'axios'

const WS_KEY = 'crm-workspace-id'

function base () {
  return Vue.prototype.$endpoint + 'sales-crm'
}

export function storedWorkspaceId () {
  try { return Number(localStorage.getItem(WS_KEY)) || null } catch (e) { return null }
}

export function storeWorkspaceId (id) {
  try { localStorage.setItem(WS_KEY, String(id)) } catch (e) { /* private mode */ }
}

let workspaceId = storedWorkspaceId()

export function setWorkspaceId (id) {
  workspaceId = id
  storeWorkspaceId(id)
}

function serialize (params) {
  const parts = []
  Object.keys(params || {}).forEach(key => {
    const v = params[key]
    if (v === undefined || v === null || v === '') return
    if (Array.isArray(v)) {
      v.forEach(item => parts.push(encodeURIComponent(key + '[]') + '=' + encodeURIComponent(item)))
    } else if (typeof v === 'object') {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(JSON.stringify(v)))
    } else if (typeof v === 'boolean') {
      parts.push(encodeURIComponent(key) + '=' + (v ? 1 : 0))
    } else {
      parts.push(encodeURIComponent(key) + '=' + encodeURIComponent(v))
    }
  })
  return parts.join('&')
}

function request (method, path, { params, data, noWorkspace, responseType } = {}) {
  const headers = { Accept: 'application/json' }
  if (!noWorkspace && workspaceId) headers['X-Workspace-Id'] = workspaceId
  return axios({
    method,
    url: base() + path,
    params,
    data,
    headers,
    responseType,
    paramsSerializer: serialize
  }).then(res => {
    if (responseType === 'blob') return res.data
    const body = res.data
    return body && Object.prototype.hasOwnProperty.call(body, 'data') ? body.data : body
  })
}

/** Normalised error: { status, message, code, payload, fields } */
export function crmError (err) {
  const res = err && err.response
  const body = (res && res.data) || {}
  const errors = body.errors || {}
  return {
    status: res ? res.status : 0,
    message: body.message || (err && err.message) || '',
    code: errors.code || null,
    payload: errors,
    // Laravel FormRequest: { field: [msg] }
    fields: errors.code ? {} : Object.keys(errors).reduce((acc, k) => {
      acc[k] = Array.isArray(errors[k]) ? errors[k][0] : errors[k]
      return acc
    }, {})
  }
}

const get = (p, params, o) => request('get', p, { ...o, params })
const post = (p, data, o) => request('post', p, { ...o, data })
const put = (p, data, o) => request('put', p, { ...o, data })
const del = (p, params, o) => request('delete', p, { ...o, params })

export default {
  // workspaces
  workspaces: params => get('/workspaces', params, { noWorkspace: true }),
  createWorkspace: data => post('/workspaces', data, { noWorkspace: true }),
  myInvites: () => get('/invites/mine', null, { noWorkspace: true }),
  acceptInvite: token => post(`/invites/${token}/accept`, {}, { noWorkspace: true }),
  declineInvite: token => post(`/invites/${token}/decline`, {}, { noWorkspace: true }),
  switchWorkspace: id => post(`/workspaces/${id}/switch`, {}),
  me: () => get('/me'),

  members: params => get('/members', params),
  pipelines: () => get('/pipelines'),
  pipelineStats: id => get(`/pipelines/${id}/stats`),
  reorderStages: (id, ids) => put(`/pipelines/${id}/stages/reorder`, { ids }),
  leadSources: () => get('/lead-sources'),
  lossReasons: () => get('/loss-reasons'),
  resolveForm: params => get('/forms/resolve', params),

  // leads
  leads: params => get('/leads', params),
  lead: id => get(`/leads/${id}`),
  createLead: data => post('/leads', data),
  updateLead: (id, data) => put(`/leads/${id}`, data),
  convertLead: (id, data) => post(`/leads/${id}/convert`, data),
  rejectLead: (id, data) => post(`/leads/${id}/reject`, data),
  restoreLead: id => post(`/leads/${id}/restore`, {}),

  // deals
  deals: params => get('/deals', params),
  deal: id => get(`/deals/${id}`),
  createDeal: data => post('/deals', data),
  updateDeal: (id, data) => put(`/deals/${id}`, data),
  moveDeal: (id, data) => post(`/deals/${id}/move`, data),
  pauseDeal: (id, data) => post(`/deals/${id}/pause`, data),
  resumeDeal: id => post(`/deals/${id}/resume`, {}),
  reopenDeal: (id, data) => post(`/deals/${id}/reopen`, data),
  copyDeal: id => post(`/deals/${id}/copy`, {}),
  deleteDeal: id => del(`/deals/${id}`),
  addDealProduct: (id, data) => post(`/deals/${id}/products`, data),
  removeDealProduct: (id, pid) => del(`/deals/${id}/products/${pid}`),
  products: params => get('/products', params),

  // views
  kanban: params => get('/views/kanban', params),
  kanbanColumn: params => get('/views/kanban/column', params),
  timeline: params => get('/views/timeline', params),
  savedViews: params => get('/saved-views', params),
  saveView: (data, id) => (id ? put(`/saved-views/${id}`, data) : post('/saved-views', data)),
  deleteView: id => del(`/saved-views/${id}`),

  // activities
  activities: params => get('/activities', params),
  createActivity: data => post('/activities', data),
  completeActivity: (id, data) => post(`/activities/${id}/complete`, data),
  cancelActivity: (id, data) => post(`/activities/${id}/cancel`, data),
  reopenActivity: id => post(`/activities/${id}/reopen`, {}),
  snoozeActivity: (id, data) => post(`/activities/${id}/snooze`, data),
  myWork: () => get('/my-work'),

  // records / bulk / reports
  archive: (type, id) => post(`/records/${type}/${id}/archive`, {}),
  unarchive: (type, id) => post(`/records/${type}/${id}/unarchive`, {}),
  undelete: (type, id) => post(`/records/${type}/${id}/undelete`, {}),
  bulk: (action, data) => post(`/bulk/${action}`, data),
  forecast: params => get('/reports/forecast', params),
  summary: params => get('/reports/summary', params),
  auditLogs: params => get('/audit-logs', params),
  search: params => get('/search', params),
  exportList: params => request('get', '/exports', { params, responseType: 'blob' })
}
